import React from 'react';
import { V1FormQuestion } from './V1FormQuestion';
import { FormContext } from '../../../../public_home/components/forms/FormContext';
import type { Question, FormResponses } from '../../../../types/publicForm.types';

interface V1EventFormQuestionsProps {
  questions: Question[];
  formData: FormResponses;
  validationErrors: Record<string, string>;
  touchedFields: Set<string>;
  visibleChildQuestions: Set<string>;
  onChange: (questionId: string, value: any) => void;
  onBlur: (questionId: string) => void;
  primaryColor?: string;
}

/**
 * V1 Event Form Questions Component - FROZEN
 * 
 * Renders ALL questions in a single scrollable view.
 * V1 forms do NOT have pages - all questions are visible at once.
 * Questions may be organized into sections but are all on one page.
 */
export const V1EventFormQuestions: React.FC<V1EventFormQuestionsProps> = ({
  questions,
  formData,
  validationErrors,
  touchedFields,
  visibleChildQuestions,
  onChange,
  onBlur,
  primaryColor = '#3B82F6',
}) => {
  // Group questions by section if they have one
  const questionsBySection = questions.reduce((acc, question) => {
    const section = question.section || 'default';
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(question);
    return acc;
  }, {} as Record<string, Question[]>);

  const sections = Object.keys(questionsBySection);
  const hasMultipleSections = sections.length > 1 || (sections.length === 1 && sections[0] !== 'default');

  return (
    <div className="space-y-6">
      {hasMultipleSections ? (
        // Render with section headers
        sections.map((section, sectionIndex) => (
          <div key={section} className="space-y-4">
            {section !== 'default' && (
              <div className="border-b border-gray-200 pb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {section}
                </h3>
              </div>
            )}
            <div className="space-y-4">
              {questionsBySection[section].map((question) => (
                <V1FormQuestion
                  key={question.id}
                  question={question}
                  primaryColor={primaryColor}
                />
              ))}
            </div>
            {sectionIndex < sections.length - 1 && (
              <div className="mt-6" />
            )}
          </div>
        ))
      ) : (
        // Render without sections
        <div className="space-y-4">
          {questions.map((question) => (
            <V1FormQuestion
              key={question.id}
              question={question}
              primaryColor={primaryColor}
            />
          ))}
        </div>
      )}
    </div>
  );
};